import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import {
  extractPeriodStartAndEndTimesWithTimezone,
  getAnswerValuesFromItems
} from 'components/Assessments/helpers'
import {
  convertAppointmentTimeFormDataToFlatAnswers,
  convertTimeToUTCString
} from 'components/Assessments/helpers.uncontrolled'
import { CustomScheduleVisit } from 'components/Assessments/components/VisitScheduling/types'
import { VisitSchedulingFormField } from './formField'
import { VisitSchedulingQuestionnaireItem } from './questionnaire'
import {
  convertCustomScheduleToFlatAnswers,
  convertOtherAppointmentsToFlatAnswers,
  extractCustomSchedule,
  extractCustomScheduleByWeekday,
  extractOtherAppointments
} from './helpers'

/**
 * Visit Scheduling subsection adapter for FUA Assessment
 * Handles visit-scheduling extraction and transformation
 */
export class VisitSchedulingAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const wakeupTime = getAnswerValuesFromItems(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME
    )?.at(0)

    const bedtime = getAnswerValuesFromItems(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME
    )?.at(0)

    const schoolTimes = getAnswerValuesFromItems(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY
    )

    const schoolTimesPeriod = extractPeriodStartAndEndTimesWithTimezone(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_START,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_END,
      options?.caregiverTimezone
    )

    const otherAppointments = extractOtherAppointments(flatAnswersArray, options)
    const customSchedule = extractCustomSchedule(flatAnswersArray, options)
    const customScheduleByWeekday = extractCustomScheduleByWeekday(customSchedule)
    const editedCustomSchedule: CustomScheduleVisit[] = []
    const additionalNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES
    )?.at(0)
    const changeReason = getAnswerValuesFromItems(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON
    )?.at(0)
    const initialsAuthorization = getAnswerValuesFromItems(
      flatAnswersArray,
      VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_INITIALS_AUTHORIZATION
    )?.at(0)

    return {
      [VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME]: wakeupTime,
      [VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME]: bedtime,
      [VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY]: schoolTimes,
      [VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD]:
        schoolTimesPeriod,
      [VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS]:
        otherAppointments,
      // Populate both daily and weekly visit schedule fields from FHIR
      // Daily mode uses flat array, weekly mode uses grouped by weekday
      [VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS]: customSchedule,
      [VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY]:
        customScheduleByWeekday,
      [VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_EDITED_VISITS]:
        editedCustomSchedule,
      [VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES]: additionalNotes,
      [VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON]: changeReason,
      [VisitSchedulingFormField.VISIT_SCHEDULING_INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Use class methods to access form data
    const wakeupTime = this.formDataFieldExists(
      VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME
    )
      ? this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME
        )
      : undefined
    const wakeUpTimeUTC = wakeupTime ? convertTimeToUTCString(wakeupTime) : undefined

    const bedtime = this.formDataFieldExists(
      VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME
    )
      ? this.getFormDataField(VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME)
      : undefined
    const bedtimeUTC = bedtime ? convertTimeToUTCString(bedtime) : undefined

    const schoolTimesPeriod = this.formDataFieldExists(
      VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD
    )
      ? this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD
        )
      : undefined
    const schoolTimesPeriodUTC = schoolTimesPeriod
      ? convertAppointmentTimeFormDataToFlatAnswers(schoolTimesPeriod)
      : undefined

    const otherAppointments = this.formDataFieldExists(
      VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS
    )
      ? convertOtherAppointmentsToFlatAnswers(
          this.getFormDataField(
            VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS
          )
        )
      : undefined

    // Handle both daily and weekly visit schedule modes
    // Weekly mode: visits grouped by weekday (preferred for weekly/shift scheduling)
    // Daily mode: flat array of visits (used for daily/visit scheduling)
    const visitsByWeekday = this.formDataFieldExists(
      VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY
    )
      ? this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY
        )
      : undefined
    const dailyVisits = this.formDataFieldExists(
      VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS
    )
      ? this.getFormDataField(VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS)
      : undefined

    // Determine which source to use based on what has data
    // Prioritize visitsByWeekday if it has data, otherwise use dailyVisits
    let flatVisits: CustomScheduleVisit[] | undefined = undefined
    if (visitsByWeekday && visitsByWeekday.length > 0) {
      // Weekly mode: flatten the grouped structure
      flatVisits = visitsByWeekday.flatMap(({ visits }) =>
        visits.filter((visit) => visit?.period && visit?.caregiverReference)
      )
    } else if (dailyVisits && dailyVisits.length > 0) {
      // Daily mode: already flat
      flatVisits = dailyVisits.map((visit) => ({
        ...visit,
        weekday: null
      }))
    }
    const customSchedule = flatVisits ? convertCustomScheduleToFlatAnswers(flatVisits) : undefined
    if (customSchedule) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS,
        value: customSchedule
      })
    }

    if (wakeUpTimeUTC !== undefined) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME,
        value: wakeUpTimeUTC
      })
    }

    if (bedtimeUTC !== undefined) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME,
        value: bedtimeUTC
      })
    }

    if (
      this.formDataFieldExists(
        VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY
      )
    ) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY,
        value: this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY
        )
      })
    }

    if (schoolTimesPeriodUTC) {
      mutations.push({
        linkId:
          VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_START,
        value: schoolTimesPeriodUTC?.appointmentTimeValues?.at(0)
      })
      mutations.push({
        linkId:
          VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_END,
        value: schoolTimesPeriodUTC?.appointmentTimeValues?.at(1)
      })
    }

    if (otherAppointments !== undefined) {
      mutations.push({
        linkId:
          VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS,
        value: otherAppointments
      })
    }

    if (
      this.formDataFieldExists(
        VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES
      )
    ) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES,
        value: this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES
        )
      })
    }

    if (
      this.formDataFieldExists(
        VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON
      )
    ) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON,
        value: this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON
        )
      })
    }

    if (
      this.formDataFieldExists(VisitSchedulingFormField.VISIT_SCHEDULING_INITIALS_AUTHORIZATION)
    ) {
      mutations.push({
        linkId: VisitSchedulingQuestionnaireItem.VISIT_SCHEDULING_INITIALS_AUTHORIZATION,
        value: this.getFormDataField(
          VisitSchedulingFormField.VISIT_SCHEDULING_INITIALS_AUTHORIZATION
        )
      })
    }

    return mutations
  }
}
